import { ApiRoutineRule, ApiRoutineSchedule } from '../types/api/types';
import { getDateTime } from '../lib/dateTime';
import {
  ContentFilteringRoutineRule,
  RoutineRule,
  ROUTINE_RULE_TYPE,
  ScreenFreeTimeRoutineRule,
} from '../types';
import { ROUTINE_BLOCK_TYPE } from './routineBlockType';

const ROUTINE_WEEKDAY_MAP: Record<string, number> = {
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
  SU: 7,
};

const parseScheduleStartTime = (
  startTime: string
): { hour: number; minute: number; second: number } | undefined => {
  const [hour, minute, second = 0] = startTime.split(':').map(Number);

  const isInRange = (value: number, max: number): boolean =>
    Number.isInteger(value) && value >= 0 && value <= max;

  if (
    !isInRange(hour, 23) ||
    !isInRange(minute, 59) ||
    !isInRange(second, 59)
  ) {
    return undefined;
  }

  return { hour, minute, second };
};

const isDateRangeAllowed = (
  schedule: ApiRoutineSchedule,
  date: string | null
): boolean => {
  if (!date) {
    return false;
  }

  if (schedule.from_date && date < schedule.from_date) {
    return false;
  }

  if (schedule.to_date && date > schedule.to_date) {
    return false;
  }

  return true;
};

const getScheduleInterval = (
  schedule: ApiRoutineSchedule,
  nowMs: number,
  timezone: string
): { startsAtMs: number; endsAtMs: number } | undefined => {
  const startTime = parseScheduleStartTime(schedule.start_time);
  if (!startTime || schedule.duration_minutes <= 0) {
    return undefined;
  }

  const nowDateTime = getDateTime(nowMs, timezone);
  for (const weekday of schedule.weekdays) {
    const weekdayNumber = ROUTINE_WEEKDAY_MAP[weekday];
    if (!weekdayNumber) {
      continue;
    }

    const daysDiff = weekdayNumber - nowDateTime.weekday;
    const startDateTime = nowDateTime
      .startOf('day')
      .plus({ days: daysDiff > 0 ? daysDiff - 7 : daysDiff })
      .set({
        hour: startTime.hour,
        minute: startTime.minute,
        second: startTime.second,
        millisecond: 0,
      });
    const endDateTime = startDateTime.plus({
      minutes: schedule.duration_minutes,
    });

    if (
      isDateRangeAllowed(schedule, startDateTime.toISODate()) &&
      nowDateTime.toMillis() >= startDateTime.toMillis() &&
      nowDateTime.toMillis() < endDateTime.toMillis()
    ) {
      return {
        startsAtMs: startDateTime.toMillis(),
        endsAtMs: endDateTime.toMillis(),
      };
    }
  }

  return undefined;
};

const isScheduleOngoing = (
  schedule: ApiRoutineSchedule,
  nowMs: number,
  timezone: string
): boolean => getScheduleInterval(schedule, nowMs, timezone) !== undefined;

const isScheduledRoutineActive = (
  routine: ApiRoutineRule,
  schedule: ApiRoutineSchedule,
  nowMs: number,
  timezone: string,
  options: { skipRoutineEligibilityCheck?: boolean } = {}
): boolean => {
  if (!options.skipRoutineEligibilityCheck) {
    if (
      routine.enabled !== true ||
      routine.paused ||
      schedule.enabled !== true
    ) {
      return false;
    }
  } else if (schedule.enabled === false) {
    return false;
  }

  return isScheduleOngoing(schedule, nowMs, timezone);
};

const routineHasOngoingSchedule = (
  routine: ApiRoutineRule,
  nowMs: number,
  timezone: string,
  skipRoutineEligibilityCheck: boolean
): boolean =>
  routine.schedules.some(schedule =>
    isScheduledRoutineActive(routine, schedule, nowMs, timezone, {
      skipRoutineEligibilityCheck,
    })
  );

const isMappableRoutine = (routine: ApiRoutineRule): boolean => {
  const blockType = routine.policy?.block_type;
  return (
    blockType === ROUTINE_BLOCK_TYPE.FILTER_ROUTINE ||
    blockType === ROUTINE_BLOCK_TYPE.LOCK_NAVIGATION ||
    blockType === ROUTINE_BLOCK_TYPE.LOCK_DEVICE
  );
};

const isScreenFreeTimeRoutine = (routine: ApiRoutineRule): boolean => {
  const blockType = routine.policy?.block_type;
  return (
    blockType === ROUTINE_BLOCK_TYPE.LOCK_NAVIGATION ||
    blockType === ROUTINE_BLOCK_TYPE.LOCK_DEVICE
  );
};

const isEligibleForNextScreenFreeTimeStart = (
  routine: ApiRoutineRule
): boolean =>
  isScreenFreeTimeRoutine(routine) &&
  routine.enabled === true &&
  !routine.paused;

const pickSoonestMs = (
  currentSoonestMs: number | undefined,
  candidateMs: number
): number =>
  currentSoonestMs === undefined || candidateMs < currentSoonestMs
    ? candidateMs
    : currentSoonestMs;

const WEEK_LOOK_AHEAD_DAY_OFFSETS = [0, 7] as const;

const considerNextScheduleStartCandidate = (
  schedule: ApiRoutineSchedule,
  weekdayNumber: number,
  nowDateTime: ReturnType<typeof getDateTime>,
  startTime: { hour: number; minute: number; second: number },
  nowMs: number,
  horizonMs: number,
  soonestMs: number | undefined
): number | undefined => {
  const baseDaysDiff = weekdayNumber - nowDateTime.weekday;
  let nextSoonestMs = soonestMs;

  for (const offset of WEEK_LOOK_AHEAD_DAY_OFFSETS) {
    const candidate = nowDateTime
      .startOf('day')
      .plus({ days: baseDaysDiff + offset })
      .set({
        hour: startTime.hour,
        minute: startTime.minute,
        second: startTime.second,
        millisecond: 0,
      });

    const candidateMs = candidate.toMillis();
    if (candidateMs <= nowMs || candidateMs > horizonMs) {
      continue;
    }
    if (!isDateRangeAllowed(schedule, candidate.toISODate())) {
      continue;
    }
    nextSoonestMs = pickSoonestMs(nextSoonestMs, candidateMs);
  }

  return nextSoonestMs;
};

/**
 * Returns the millisecond timestamp of the soonest next screen-free-time
 * routine start strictly after `nowMs` and within `nowMs + lookaheadMs`,
 * or `undefined` if no such start exists. Mirrors KASA's
 * `findNextLockRoutineStartsInSeconds`.
 *
 * Only `LOCK_NAVIGATION` / `LOCK_DEVICE` routines are considered; filter
 * routines (which do not lock the screen) are ignored, matching the Kids
 * App behaviour for the 15-/5-minute pre-start notifications.
 */
export const findNextScreenFreeTimeStartMs = (
  routines: readonly ApiRoutineRule[],
  nowMs: number,
  timezone: string,
  lookaheadMs: number
): number | undefined => {
  if (lookaheadMs <= 0) {
    return undefined;
  }

  const horizonMs = nowMs + lookaheadMs;
  let soonestStartMs: number | undefined;

  for (const routine of routines) {
    if (!isEligibleForNextScreenFreeTimeStart(routine)) {
      continue;
    }
    for (const schedule of routine.schedules) {
      if (schedule.enabled === false) {
        continue;
      }
      const startMs = nextScheduleStartAfter(
        schedule,
        nowMs,
        timezone,
        horizonMs
      );
      if (startMs !== undefined) {
        soonestStartMs = pickSoonestMs(soonestStartMs, startMs);
      }
    }
  }

  return soonestStartMs;
};

const nextScheduleStartAfter = (
  schedule: ApiRoutineSchedule,
  nowMs: number,
  timezone: string,
  horizonMs: number
): number | undefined => {
  const startTime = parseScheduleStartTime(schedule.start_time);
  if (!startTime || schedule.duration_minutes <= 0) {
    return undefined;
  }

  const nowDateTime = getDateTime(nowMs, timezone);
  let soonestMs: number | undefined;

  for (const weekday of schedule.weekdays) {
    const weekdayNumber = ROUTINE_WEEKDAY_MAP[weekday];
    if (!weekdayNumber) {
      continue;
    }

    soonestMs = considerNextScheduleStartCandidate(
      schedule,
      weekdayNumber,
      nowDateTime,
      startTime,
      nowMs,
      horizonMs,
      soonestMs
    );
  }

  return soonestMs;
};

/**
 * Picks the single active routine using the same override precedence as KASA
 * (`RoutineScheduleExtensions.findActiveRoutine`). Routines with missing
 * policies or unsupported block types are skipped so a malformed winner
 * cannot strip `activeUntil` from another otherwise-active routine via
 * {@link mapRoutineRules}.
 */
export const findActiveApiRoutine = (
  routines: readonly ApiRoutineRule[],
  nowMs: number,
  timezone: string
): ApiRoutineRule | undefined => {
  const mappable = routines.filter(isMappableRoutine);
  const routinesWithOverride = mappable.filter(routine =>
    routine.schedules.some(schedule => schedule.overrides === true)
  );
  const enabledRoutines = mappable.filter(routine => !routine.paused);
  const candidates = [...routinesWithOverride, ...enabledRoutines];

  return candidates.find(routine => {
    const skipRoutineEligibilityCheck = routinesWithOverride.includes(routine);
    return routineHasOngoingSchedule(
      routine,
      nowMs,
      timezone,
      skipRoutineEligibilityCheck
    );
  });
};

const mapRoutineDefaultAction = (
  action: 0 | 1 | null | undefined
): 'ALLOW_ALL' | 'BLOCK_ALL' => (action === 1 ? 'BLOCK_ALL' : 'ALLOW_ALL');

const getActiveUntil = (
  routine: ApiRoutineRule,
  nowMs: number,
  timezone: string
): string | undefined => {
  const activeSchedule = routine.schedules.find(schedule =>
    isScheduledRoutineActive(routine, schedule, nowMs, timezone, {
      skipRoutineEligibilityCheck: routine.schedules.some(
        candidate => candidate.overrides === true
      ),
    })
  );
  const interval = activeSchedule
    ? getScheduleInterval(activeSchedule, nowMs, timezone)
    : undefined;

  return interval ? new Date(interval.endsAtMs).toISOString() : undefined;
};

const mapRoutineRule = (
  routine: ApiRoutineRule,
  nowMs: number,
  timezone: string,
  activeRoutineId: string | undefined
): RoutineRule | undefined => {
  const policy = routine.policy;
  if (!policy) {
    return undefined;
  }

  const activeUntil =
    activeRoutineId === routine.uid
      ? getActiveUntil(routine, nowMs, timezone)
      : undefined;

  // When the backend omits the flag, keep counting toward the quota so that
  // legacy responses do not silently pause the daily quota counter.
  const countsTowardsDailyTimeQuota =
    policy.counts_towards_daily_time_quota ?? true;

  const { block_type: blockType } = policy;

  if (
    blockType === ROUTINE_BLOCK_TYPE.LOCK_NAVIGATION ||
    blockType === ROUTINE_BLOCK_TYPE.LOCK_DEVICE
  ) {
    const screenFreeTimeRule: ScreenFreeTimeRoutineRule = {
      id: routine.uid,
      name: routine.name,
      type: ROUTINE_RULE_TYPE.SCREEN_FREE_TIME,
      activeUntil,
      countsTowardsDailyTimeQuota,
    };
    return screenFreeTimeRule;
  }

  if (blockType !== ROUTINE_BLOCK_TYPE.FILTER_ROUTINE) {
    return undefined;
  }

  const defaultAction = policy.web_rules?.default_action;
  const contentFilteringRule: ContentFilteringRoutineRule = {
    id: routine.uid,
    name: routine.name,
    type: ROUTINE_RULE_TYPE.CONTENT_FILTERING,
    activeUntil,
    defaultDomainCategoriesAction: mapRoutineDefaultAction(defaultAction),
    domainCategoriesExceptions:
      policy.web_rules?.web_rules_list
        .filter(rule => rule.action !== defaultAction)
        .map(rule => rule.category)
        .filter(
          (category): category is number =>
            category !== undefined && category !== null
        ) ?? [],
    countsTowardsDailyTimeQuota,
  };
  return contentFilteringRule;
};

export const mapRoutineRules = (
  routines: readonly ApiRoutineRule[],
  nowMs: number,
  timezone: string
): RoutineRule[] => {
  const activeRoutine = findActiveApiRoutine(routines, nowMs, timezone);

  return routines
    .map(routine =>
      mapRoutineRule(routine, nowMs, timezone, activeRoutine?.uid)
    )
    .filter((routine): routine is RoutineRule => routine !== undefined);
};
